<template>
  <div class="vol-filter" ref="root">
    <button
      type="button"
      class="vol-filter__trigger"
      :class="{ 'is-active': isActive, 'is-open': open, 'is-count': isCountValue }"
      :title="triggerTitle"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click="toggle"
    >
      <span class="vol-filter__label">{{ label }}</span>
      <span v-if="isActive" class="vol-filter__value">{{ valueText }}</span>
      <i
        v-if="isActive"
        class="vol-filter__clear fas fa-times"
        role="button"
        :title="'Limpar ' + label.toLowerCase()"
        @click.stop="clearAll"
      ></i>
      <i v-else class="vol-filter__caret fas fa-chevron-down"></i>
    </button>

    <ul v-show="open" class="vol-filter__menu" :style="menuStyle" role="listbox">
      <li
        class="vol-filter__option vol-filter__option--all"
        :class="{ 'is-selected': !isActive }"
        role="option"
        @click="clearAll"
      >
        {{ allText }}
      </li>
      <li
        v-for="item in items"
        :key="String(item.value)"
        class="vol-filter__option"
        :class="{ 'is-selected': isSelected(item.value) }"
        role="option"
        :aria-selected="isSelected(item.value) ? 'true' : 'false'"
        @click="select(item.value)"
      >
        <i
          class="vol-filter__check"
          :class="checkIcon(item.value)"
        ></i>
        <span class="vol-filter__option-text">{{ item.text }}</span>
      </li>
      <li v-if="!items.length" class="vol-filter__empty">Nenhuma opção disponível</li>
    </ul>
  </div>
</template>

<script type="plain/text">
/**
 * Campo de filtro da vitrine de vagas de voluntariado.
 *
 * Um "pill" com largura definida pelo CONTEUDO, e nao por uma fracao fixa da barra:
 * era a divisao em 5 colunas iguais que cortava os rotulos longos ("Nome da
 * instituicao", "Tipo da vaga") no desktop e no mobile. Com largura natural e
 * quebra de linha, nada e truncado em nenhuma largura de tela.
 *
 * O pill tambem mostra o ESTADO: inativo fica com contorno, ativo fica preenchido,
 * exibe o valor escolhido (ou a contagem, no multiplo) e ganha um "x" para limpar
 * so aquele filtro. Antes todos os campos tinham a mesma aparencia, selecionados
 * ou nao.
 *
 * Nao usa v-select do Vuetify de proposito: as regras globais de combobox do
 * skin.styl conflitam com o campo dentro dessa barra.
 *
 * Emite `input` com o array de valores (multiple) ou com o valor unico (single).
 */
export default {
  props: {
    /** Nome do filtro. Ex.: "Datas" */
    label: { type: String, required: true },
    /** Opcoes no formato { text, value } */
    items: { type: Array, default: () => [] },
    /** Array de valores (multiple) ou valor unico (single) */
    value: { default: null },
    multiple: { type: Boolean, default: false },
    /** Rotulo da opcao que limpa o filtro. Ex.: "Todas as datas" */
    allText: { type: String, default: "Todos" },
  },
  data() {
    return {
      open: false,
      menuShift: 0,
    };
  },
  computed: {
    selectedValues() {
      if (!this.multiple) return this.value === null || this.value === "" ? [] : [this.value];
      return Array.isArray(this.value) ? this.value : [];
    },
    isActive() {
      return this.selectedValues.length > 0;
    },
    /** No pill: o valor quando e um so, a contagem quando sao varios. */
    valueText() {
      const selected = this.selectedValues;
      if (!selected.length) return "";
      if (selected.length === 1) return this.textOf(selected[0]);
      return selected.length + " selecionadas";
    },
    /**
     * O pill esta mostrando uma contagem ("3 selecionadas") em vez de um valor?
     * Nesse caso o rotulo tem de continuar visivel mesmo no mobile -- sem ele,
     * dois filtros multiplos ficariam identicos na tela.
     */
    isCountValue() {
      return this.selectedValues.length > 1;
    },
    /** Deslocamento horizontal aplicado para o menu nao vazar da tela. */
    menuStyle() {
      return this.menuShift ? { transform: 'translateX(' + this.menuShift + 'px)' } : {};
    },
    triggerTitle() {
      if (!this.isActive) return this.label;
      return this.label + ": " + this.selectedValues.map((value) => this.textOf(value)).join(", ");
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    textOf(value) {
      const item = this.items.find((i) => i.value === value);
      return item ? item.text : value;
    },
    isSelected(value) {
      return this.selectedValues.indexOf(value) !== -1;
    },
    checkIcon(value) {
      if (this.multiple) return this.isSelected(value) ? "fas fa-check-square" : "far fa-square";
      return this.isSelected(value) ? "fas fa-dot-circle" : "far fa-circle";
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.$nextTick(this.keepMenuInsideViewport);
      else this.menuShift = 0;
    },
    /**
     * O menu abre alinhado a esquerda do pill. Nos pills mais a direita da barra isso
     * o jogava para fora da tela -- e em telas estreitas ancorar pela direita apenas
     * troca o lado do vazamento (menu de 308px numa tela de 375px nao cabe em nenhum
     * dos dois cantos). Aqui ele continua ancorado ao pill e e apenas deslocado o
     * minimo necessario para ficar visivel.
     */
    keepMenuInsideViewport() {
      const menu = this.$el.querySelector(".vol-filter__menu");
      if (!menu) return;

      this.menuShift = 0;

      this.$nextTick(() => {
        const rect = menu.getBoundingClientRect();
        const folga = 8;
        // clientWidth (largura de layout, sem a barra de rolagem) e nao innerWidth:
        // innerWidth oscila sob emulacao de dispositivo e daria um deslocamento errado.
        const larguraTela = document.documentElement.clientWidth;

        if (rect.right > larguraTela - folga) {
          this.menuShift = Math.round(-(rect.right - (larguraTela - folga)));
        } else if (rect.left < folga) {
          this.menuShift = Math.round(folga - rect.left);
        }
      });
    },
    onDocumentClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) this.open = false;
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.open = false;
    },
    select(value) {
      if (!this.multiple) {
        this.$emit("input", value);
        this.open = false;
        return;
      }

      const selected = this.selectedValues.slice();
      const index = selected.indexOf(value);
      if (index === -1) selected.push(value);
      else selected.splice(index, 1);

      // multiplo mantem o menu aberto: a pessoa costuma marcar varias datas seguidas
      this.$emit("input", selected);
    },
    clearAll() {
      this.$emit("input", this.multiple ? [] : "");
      this.open = false;
    },
  },
};
</script>

<style scoped>
.vol-filter {
  position: relative;
  /* largura pelo conteudo: e o que impede o corte dos rotulos */
  flex: 0 1 auto;
}

.vol-filter__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  max-width: 100%;
  height: 4.2rem;
  padding: 0 1.6rem;
  border: 1px solid rgba(var(--theme-primaria));
  border-radius: 2.1rem;
  background-color: #fff;
  color: rgba(var(--theme-primaria));
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.vol-filter__trigger:hover {
  box-shadow: 0 0.2rem 0.6rem #00000026;
}

.vol-filter__trigger.is-open {
  box-shadow: 0 0.2rem 0.8rem #00000033;
}

.vol-filter__trigger.is-active {
  background-color: rgba(var(--theme-primaria));
  color: #fff;
}

.vol-filter__label {
  white-space: nowrap;
}

.vol-filter__trigger.is-active .vol-filter__label::after {
  content: ":";
}

/* o valor escolhido e a unica parte que pode encurtar, e com reticencias */
.vol-filter__value {
  overflow: hidden;
  max-width: 18rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 600;
}

.vol-filter__caret {
  font-size: 1.1rem;
  opacity: 0.75;
}

.vol-filter__clear {
  flex-shrink: 0;
  padding: 0.3rem;
  font-size: 1.2rem;
  opacity: 0.85;
}

.vol-filter__clear:hover {
  opacity: 1;
}

.vol-filter__menu {
  position: absolute;
  z-index: 1001;
  top: calc(100% + 0.6rem);
  left: 0;
  margin: 0;
  padding: 0.6rem 0;
  min-width: 24rem;
  max-width: 90vw;
  max-height: 32rem;
  overflow-y: auto;
  list-style: none;
  background-color: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 0.4rem 1.6rem #00000029;
  text-align: left;
}

.vol-filter__option {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.6rem;
  font-size: 1.4rem;
  color: #4a4a4a;
  cursor: pointer;
  white-space: nowrap;
}

.vol-filter__option:hover {
  background-color: #f4f4f6;
}

.vol-filter__option.is-selected {
  color: rgba(var(--theme-primaria));
  font-weight: 600;
}

.vol-filter__option--all {
  margin-bottom: 0.4rem;
  padding-left: 1.6rem;
  border-bottom: 1px solid #ececef;
  padding-bottom: 1.2rem;
}

.vol-filter__check {
  flex-shrink: 0;
  width: 1.6rem;
  color: rgba(var(--theme-primaria));
  font-size: 1.4rem;
}

.vol-filter__option-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.vol-filter__empty {
  padding: 1rem 1.6rem;
  font-size: 1.3rem;
  color: #8a8a8a;
}

@media (max-width: 768px) {
  .vol-filter__trigger {
    height: 3.8rem;
    padding: 0 1.2rem;
    font-size: 1.3rem;
  }

  /* Filtro com UM valor escolhido mostra so o valor: em 375px o par "rotulo: valor"
     nao cabe e o valor saia com reticencias (ex.: "Instituto Energia do Sab...").
     O proprio valor identifica o filtro. Quando o pill mostra contagem
     ("2 selecionadas"), o rotulo fica -- sem ele dois filtros multiplos ficariam
     iguais na tela. */
  .vol-filter__trigger.is-active:not(.is-count) .vol-filter__label {
    display: none;
  }

  .vol-filter__value {
    max-width: calc(100vw - 9rem);
  }

  .vol-filter__menu {
    min-width: 22rem;
    max-height: 26rem;
  }
}
</style>
